// policyControls — reading an enforcement policy as a ladder of CONTROL POINTS.
//
// Soft and hard are not two kinds of budget. One €2m authorization can carry both: notify
// at 80%, review a projected breach, require approval above the amount. They are steps in
// the policy that governs the authorization, so this module renders them as steps.
//
// The vocabulary is authorization, not shutdown. A hard step does not stop a workload; it
// means spend above the amount is no longer financially authorized without an explicit
// decision. Every string here has to be true in that sense.

const ORDER = { soft: 0, predictive: 1, hard: 2, spike: 3 };

const pct = (v) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? `${Math.round(v)}%` : null);

// controlPoints returns the policy's steps as short, ordered sentences.
export function controlPoints(policy) {
  if (!policy) return [];
  const steps = Array.isArray(policy.steps) ? policy.steps : [];
  const seen = new Set();
  const out = [];

  const sorted = [...steps].sort(
    (a, b) => (ORDER[a?.trigger_level] ?? 9) - (ORDER[b?.trigger_level] ?? 9)
  );

  for (const step of sorted) {
    const approval = step?.action === "approval_gate";
    let text = null;
    switch (step?.trigger_level) {
      case "soft": {
        const at = pct(policy.soft_pct);
        text = approval
          ? `Approval required at ${at || "the soft threshold"} of the authorized amount`
          : `Warn at ${at || "the soft threshold"} of the authorized amount`;
        break;
      }
      case "hard": {
        const at = pct(policy.hard_pct);
        const where = at && at !== "100%" ? `at ${at} of the authorized amount` : "above the authorized amount";
        text = approval ? `Approval required ${where}` : `Warn ${where}`;
        break;
      }
      case "predictive":
        text = approval ? "Approval required on a projected breach" : "Review a projected breach";
        break;
      case "spike":
        text = approval ? "Approval required on a spend spike" : "Review a spend spike";
        break;
      default:
        text = null;
    }
    if (text && !seen.has(text)) {
      seen.add(text);
      out.push(text);
    }
  }
  return out;
}

// requiresApproval — whether any step turns the amount into a decision point, which is what
// makes the authorization framing ("no longer authorized without an explicit decision")
// true rather than decorative.
export function requiresApproval(policy) {
  const steps = Array.isArray(policy?.steps) ? policy.steps : [];
  return steps.some((s) => s?.action === "approval_gate");
}
